import React, { useEffect, useState } from "react";
import { currency, backend_url } from "../App";
import { adminGet, adminPost } from "../common/api/api";

const STATUS_STYLES = {
  pending:    "text-yellow-400 bg-yellow-400/10 border-yellow-400/30",
  processing: "text-blue-400 bg-blue-400/10 border-blue-400/30",
  shipped:    "text-purple-400 bg-purple-400/10 border-purple-400/30",
  delivered:  "text-green-400 bg-green-400/10 border-green-400/30",
  cancelled:  "text-red-400 bg-red-400/10 border-red-400/30",
};

const RETURN_STYLES = {
  none:      "",
  requested: "text-orange-400 bg-orange-400/10 border-orange-400/30",
  approved:  "text-green-400 bg-green-400/10 border-green-400/30",
  rejected:  "text-red-400 bg-red-400/10 border-red-400/30",
};

const STATUSES = ["pending", "processing", "shipped", "delivered", "cancelled"];

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [expanded, setExpanded] = useState(null);

  // Sample order form
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ userName: "", userEmail: "", totalAmount: "", address: "", paymentMethod: "cod" });

  const fetchOrders = async () => {
    setLoading(true);
    const data = await adminGet("/allorders");
    setOrders(Array.isArray(data) ? data : []);
    setLoading(false);
  };

  useEffect(() => { fetchOrders(); }, []);

  const updateStatus = async (id, status) => {
    await adminPost("/updateorderstatus", { id, status });
    fetchOrders();
  };

  const updateReturnStatus = async (id, status) => {
    const note = status === "rejected" ? (window.prompt("Rejection reason (optional):") || "") : "";
    await adminPost("/updatereturnstatus", { id, status, resolvedNote: note });
    fetchOrders();
  };

  const deleteOrder = async (id) => {
    if (!window.confirm("Delete this order?")) return;
    await adminPost("/deleteorder", { id });
    fetchOrders();
  };

  const addOrder = async () => {
    if (!form.userName || !form.userEmail) { alert("Name and email required"); return; }
    await adminPost("/addorder", { ...form, totalAmount: parseFloat(form.totalAmount) || 0 });
    setForm({ userName: "", userEmail: "", totalAmount: "", address: "", paymentMethod: "cod" });
    setShowForm(false);
    fetchOrders();
  };

  const filtered = filter === "all" ? orders : orders.filter((o) => o.status === filter);

  const counts = STATUSES.reduce((acc, s) => ({ ...acc, [s]: orders.filter((o) => o.status === s).length }), {});

  const inputClass = "bg-white/[0.05] border border-white/[0.1] rounded-lg px-3 py-2 text-[#e2e8f0] placeholder-[#4a5568] focus:outline-none focus:border-[#00d4ff]/60 text-sm w-full";

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white">Orders</h1>
          <p className="text-sm text-[#4a5568] mt-0.5">{orders.length} total orders</p>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="px-4 py-2 bg-gradient-to-r from-[#00d4ff] to-[#0099cc] text-[#060c18] font-bold rounded-lg text-sm hover:opacity-90 transition-opacity cursor-pointer"
        >
          + Add Order
        </button>
      </div>

      {/* Add Order Form */}
      {showForm && (
        <div className="mb-5 p-4 bg-white/[0.03] border border-white/[0.08] rounded-xl">
          <h3 className="text-sm font-semibold text-white mb-3">New Order</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
            <input className={inputClass} placeholder="Customer Name *" value={form.userName} onChange={e => setForm({ ...form, userName: e.target.value })} />
            <input className={inputClass} placeholder="Customer Email *" value={form.userEmail} onChange={e => setForm({ ...form, userEmail: e.target.value })} />
            <input className={inputClass} placeholder="Total Amount" type="number" value={form.totalAmount} onChange={e => setForm({ ...form, totalAmount: e.target.value })} />
            <select className={inputClass} style={{ background: '#0d1527' }} value={form.paymentMethod} onChange={e => setForm({ ...form, paymentMethod: e.target.value })}>
              <option value="cod">Cash on Delivery</option>
              <option value="card">Credit/Debit Card</option>
              <option value="online">Online Payment</option>
            </select>
            <input className={`${inputClass} col-span-2`} placeholder="Delivery Address" value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} />
          </div>
          <div className="flex gap-2">
            <button onClick={addOrder} className="px-4 py-2 bg-[rgba(0,212,255,0.15)] text-[#00d4ff] border border-[rgba(0,212,255,0.3)] rounded-lg text-sm hover:bg-[rgba(0,212,255,0.25)] transition-colors cursor-pointer">Save</button>
            <button onClick={() => setShowForm(false)} className="px-4 py-2 text-[#4a5568] border border-white/[0.08] rounded-lg text-sm hover:border-white/[0.2] transition-colors cursor-pointer">Cancel</button>
          </div>
        </div>
      )}

      {/* Status Stats */}
      <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mb-5">
        {STATUSES.map((s) => (
          <div key={s} className={`p-3 rounded-xl border text-center cursor-pointer transition-all ${STATUS_STYLES[s]}`} onClick={() => setFilter(filter === s ? "all" : s)}>
            <p className="text-xl font-bold">{counts[s] || 0}</p>
            <p className="text-xs capitalize mt-0.5 opacity-80">{s}</p>
          </div>
        ))}
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 mb-4 flex-wrap">
        {["all", ...STATUSES].map((s) => (
          <button key={s} onClick={() => setFilter(s)}
            className={`px-3 py-1 rounded-full text-xs font-medium border transition-all cursor-pointer capitalize ${
              filter === s ? "bg-[rgba(0,212,255,0.15)] text-[#00d4ff] border-[rgba(0,212,255,0.4)]" : "text-[#4a5568] border-white/[0.08] hover:border-white/[0.2] hover:text-[#a0aec0]"
            }`}>
            {s} {s !== "all" && `(${counts[s] || 0})`}
          </button>
        ))}
      </div>

      {/* Orders List */}
      {loading ? (
        <div className="text-[#4a5568] py-16 text-center">Loading orders...</div>
      ) : filtered.length === 0 ? (
        <div className="py-16 text-center text-[#4a5568]">No orders found.</div>
      ) : (
        <div className="space-y-3">
          {filtered.map((order) => (
            <div key={order._id} className="bg-white/[0.02] border border-white/[0.07] rounded-xl overflow-hidden">
              {/* Order Header */}
              <div
                className="flex items-center justify-between gap-4 p-4 cursor-pointer hover:bg-white/[0.02] transition-colors"
                onClick={() => setExpanded(expanded === order._id ? null : order._id)}
              >
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="text-white font-mono font-semibold text-sm">#{order._id?.slice(-8).toUpperCase()}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_STYLES[order.status] || STATUS_STYLES.pending}`}>
                    {order.status}
                  </span>
                  <span className="text-[#a0aec0] text-sm">{order.userName}</span>
                  <span className="text-[#4a5568] text-xs">{order.userEmail}</span>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-[#00d4ff] font-bold">{currency}{order.totalAmount}</p>
                  <p className="text-[#4a5568] text-xs">{order.date ? new Date(order.date).toLocaleDateString() : ""}</p>
                </div>
              </div>

              {/* Expanded Details */}
              {expanded === order._id && (
                <div className="px-4 pb-4 border-t border-white/[0.06]">
                  <div className="mt-3 grid grid-cols-2 gap-3 text-sm mb-3">
                    <div>
                      <p className="text-[#4a5568] text-xs mb-0.5">Payment</p>
                      <p className="text-[#a0aec0]">{order.paymentMethod} · <span className={order.paymentStatus === "paid" ? "text-green-400" : "text-yellow-400"}>{order.paymentStatus}</span></p>
                    </div>
                    {order.address && (
                      <div>
                        <p className="text-[#4a5568] text-xs mb-0.5">Address</p>
                        <p className="text-[#a0aec0]">{order.address}</p>
                      </div>
                    )}
                  </div>

                  {order.products?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-3">
                      {order.products.map((p, j) => (
                        <div key={j} className="flex items-center gap-2 bg-white/[0.03] border border-white/[0.06] rounded-lg px-2.5 py-1.5">
                          {p.image && <img src={String(p.image).startsWith("http") ? p.image : backend_url + p.image} alt="" className="w-7 h-7 rounded object-cover" />}
                          <div>
                            <p className="text-[#e2e8f0] text-xs font-medium">{p.name}</p>
                            <p className="text-[#4a5568] text-[10px]">×{p.quantity} · {currency}{p.price}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Return Request */}
                  {order.returnRequest && order.returnRequest.status !== "none" && (
                    <div className="mb-3 p-3 rounded-lg border border-orange-400/20 bg-orange-400/5">
                      <div className="flex items-center justify-between flex-wrap gap-2 mb-1.5">
                        <div className="flex items-center gap-2">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" className="text-orange-400" style={{color:'#fb923c'}}>
                            <polyline points="1 4 1 10 7 10"/>
                            <path d="M3.51 15a9 9 0 1 0 .49-3.32"/>
                          </svg>
                          <span className="text-xs font-bold text-orange-400">Return Request</span>
                          <span className={`text-[10px] px-2 py-0.5 rounded-full border capitalize ${RETURN_STYLES[order.returnRequest.status]}`}>
                            {order.returnRequest.status}
                          </span>
                        </div>
                        <span className="text-[#4a5568] text-[10px]">
                          {order.returnRequest.requestDate ? new Date(order.returnRequest.requestDate).toLocaleDateString() : ""}
                        </span>
                      </div>
                      {order.returnRequest.reason && (
                        <p className="text-xs text-[#a0aec0] mb-2"><span className="text-[#6b7a8d]">Reason: </span>{order.returnRequest.reason}</p>
                      )}
                      {order.returnRequest.resolvedNote && (
                        <p className="text-xs text-[#a0aec0] mb-2"><span className="text-[#6b7a8d]">Note: </span>{order.returnRequest.resolvedNote}</p>
                      )}
                      {order.returnRequest.status === "requested" && (
                        <div className="flex gap-2 mt-1.5">
                          <button
                            onClick={() => updateReturnStatus(order._id, "approved")}
                            className="text-xs px-3 py-1 rounded-lg border border-green-500/30 text-green-400 hover:bg-green-500/10 transition-all cursor-pointer"
                          >Approve Return</button>
                          <button
                            onClick={() => updateReturnStatus(order._id, "rejected")}
                            className="text-xs px-3 py-1 rounded-lg border border-red-500/20 text-red-400 hover:bg-red-500/10 transition-all cursor-pointer"
                          >Reject</button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Update Status */}
                  <div className="flex gap-1.5 flex-wrap items-center">
                    <span className="text-xs text-[#4a5568] mr-1">Update:</span>
                    {STATUSES.map((s) => (
                      <button key={s} onClick={() => updateStatus(order._id, s)}
                        className={`text-xs px-2.5 py-1 rounded-lg border transition-all cursor-pointer capitalize ${
                          order.status === s ? "bg-[rgba(0,212,255,0.12)] text-[#00d4ff] border-[rgba(0,212,255,0.3)]" : "text-[#4a5568] border-white/[0.06] hover:border-white/[0.2] hover:text-white"
                        }`}>
                        {s}
                      </button>
                    ))}
                    <button onClick={() => deleteOrder(order._id)}
                      className="text-xs px-2.5 py-1 rounded-lg border border-red-500/20 text-red-400 hover:bg-red-500/10 transition-all cursor-pointer ml-auto">
                      Delete
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminOrders;
